import React from "react";
import { ChartBar, Clock, Hash, Percent, TrendUp } from "@phosphor-icons/react";

import type { L1Dimension, L3Context } from "../TargetBuilderPanel.types";

// ---------------------------------------------------------------------------
// L4 — Derived Metrics mapping
//
// Read-only catalogue of analytics surfaced in `AnalyticsPreview` based on
// the user's L1 (behavioral dimension) + L3 (instructional context) choice.
//
// Source of truth for the analytics copy. Updating this file should be enough
// to add/remove/rename metrics — the rendering component does not need to
// change.
// ---------------------------------------------------------------------------

/**
 * Canonical, context-independent metric ids for the non-line "segment" metrics
 * that render as insight/summary cards instead of a line chart. `trend` in
 * particular collapses ~9 context-specific labels ("Duration Trend", "Latency
 * Trend", "Trend Over Time", …) onto one id so the chart-area dispatcher can
 * recognise them all. Line-chart metrics don't carry an id — they resolve by
 * title through the metric chart registry.
 */
export type MetricSegmentId =
  | "trend"
  | "trend-level-variability"
  | "mastery-status"
  | "chain-completion"
  | "trials-to-criterion"
  | "prompt-level-distribution"
  | "step-level-errors"
  | "step-acquisition-sequence"
  | "rating-distribution"
  | "latency-by-prompt-level";

export interface AnalyticItem {
  icon: React.ReactNode;
  /** Canonical segment id — set only for non-line "card" metrics. */
  id?: MetricSegmentId;
  title: string;
  description: string;
}

export const ANALYTICS_ICONS = {
  percent: React.createElement(Percent, { size: 18, weight: "regular" }),
  hash: React.createElement(Hash, { size: 18, weight: "regular" }),
  chart: React.createElement(ChartBar, { size: 18, weight: "regular" }),
  trend: React.createElement(TrendUp, { size: 18, weight: "regular" }),
  clock: React.createElement(Clock, { size: 18, weight: "regular" }),
} as const;

export type AnalyticsComboKey = `${L1Dimension}|${L3Context}`;

export const ANALYTICS_BY_L1_L3: Partial<Record<AnalyticsComboKey, AnalyticItem[]>> = {
  "frequency|discrete-trial": [
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Independent",
      description: "Percentage of trials completed without prompting",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Correct",
      description: "Percentage of trials with correct responses",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "prompt-level-distribution",
      title: "Prompt Level Distribution",
      description: "Breakdown of prompt levels used across trials",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Trials recorded so far in Baseline/Intervention",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "mastery-status",
      title: "Mastery Status",
      description: "Progress toward meeting mastery criteria",
    },
  ],
  "frequency|net": [
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Of Opportunities",
      description: "Percentage of natural opportunities with correct response",
    },
    {
      icon: ANALYTICS_ICONS.hash,
      title: "Opportunity Frequency",
      description: "How often opportunities occurred",
    },
    // % Correct / % Independent / Prompt Level Distribution / Trials to
    // Criterion (ANOW-6909): only meaningful once the target's NET count type
    // is Correct/Incorrect or Prompt Hierarchy instead of raw occurrences —
    // filtered in `getAnalyticsToggleOptions` the same way as
    // frequency+discrete-trial above.
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Correct",
      description: "Percentage of trials with correct responses",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Independent",
      description: "Percentage of trials completed without prompting",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "prompt-level-distribution",
      title: "Prompt Level Distribution",
      description: "Breakdown of prompt levels used across opportunities",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Number of trials needed to reach mastery",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Trend Over Time",
      description: "Direction and rate of change across sessions",
    },
  ],
  "frequency|task-analysis": [
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Steps Independent",
      description: "Percentage of steps completed independently",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Steps Correct",
      description: "Percentage of steps performed correctly",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "chain-completion",
      title: "Chain Completion Rate",
      description: "How often the full chain is completed",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "step-level-errors",
      title: "Step-Level Errors",
      description: "Which steps have the most errors",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "step-acquisition-sequence",
      title: "Step Acquisition Sequence",
      description: "Order in which steps were mastered",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "prompt-level-distribution",
      title: "Prompt Level Distribution",
      description: "Breakdown of prompt levels used across chain steps",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Trials recorded so far in Baseline/Intervention",
    },
  ],
  "frequency|free-operant": [
    {
      icon: ANALYTICS_ICONS.hash,
      title: "Count per Session",
      description: "Number of occurrences each session",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "Rate (per hour)",
      description: "Occurrences divided by session time",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend-level-variability",
      title: "Trend / Level / Variability",
      description: "Visual analysis metrics",
    },
  ],
  "duration|discrete-trial": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Duration Per Trial",
      description: "Average duration of behavior within each trial",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Duration Trend",
      description: "Change in duration across sessions",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Trials Meeting Duration Criterion",
      description: "Percentage of trials meeting the duration mastery criterion",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Trials recorded so far in Baseline/Intervention",
    },
  ],
  "duration|net": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Duration Per Opportunity",
      description: "Average duration within each natural opportunity",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Duration Trend",
      description: "Change in duration across sessions",
    },
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Total Duration Per Session",
      description: "Cumulative duration across all opportunities in a session",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Trials Meeting Duration Criterion",
      description: "Percentage of opportunities meeting the duration mastery criterion",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Opportunities to Criterion",
      description: "Opportunities recorded so far in Baseline/Intervention",
    },
  ],
  "duration|free-operant": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Duration",
      description: "Average length of each bout",
    },
    {
      icon: ANALYTICS_ICONS.hash,
      title: "Bout Count",
      description: "Number of behavior episodes",
    },
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Total Duration",
      description: "Sum of all bout durations",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Longest Bout",
      description: "Maximum duration in the session",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Of Session In Behavior",
      description: "Proportion of session time in behavior",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Duration Trend",
      description: "Change in total duration over sessions",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Trials Meeting Duration Criterion",
      description: "Percentage of bouts meeting the mastery duration threshold",
    },
  ],
  "latency|discrete-trial": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Latency",
      description: "Average time from SD to response",
    },
    {
      icon: ANALYTICS_ICONS.clock,
      id: "latency-by-prompt-level",
      title: "Latency By Prompt Level",
      description: "Response time broken down by prompt level",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Trials Meeting Duration Criterion",
      description: "Percentage of trials responded to within the mastery latency threshold",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Trials recorded so far in Baseline/Intervention",
    },
  ],
  "latency|net": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Response Latency",
      description: "Average time from opportunity to response",
    },
    // Latency By Prompt Level (ANOW-6909): only meaningful once the target's
    // NET count type is Prompt Hierarchy — filtered in
    // `getAnalyticsToggleOptions` by `hasPromptHierarchy`.
    {
      icon: ANALYTICS_ICONS.clock,
      id: "latency-by-prompt-level",
      title: "Latency By Prompt Level",
      description: "Response time broken down by prompt level",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Latency Trend",
      description: "Change in response latency over time",
    },
    {
      icon: ANALYTICS_ICONS.percent,
      title: "% Trials Meeting Duration Criterion",
      description: "Percentage of trials responded to within the mastery latency threshold",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Opportunities to Criterion",
      description: "Opportunities recorded so far in Baseline/Intervention",
    },
  ],
  "latency|free-operant": [
    {
      icon: ANALYTICS_ICONS.clock,
      title: "Mean Latency To Naturally Occurring Response",
      description: "Average latency to naturally occurring response",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Latency Trend",
      description: "Change in response latency over time",
    },
  ],
  "magnitude|discrete-trial": [
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Mean Trial Rating",
      description: "Average quality/intensity rating per trial",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "rating-distribution",
      title: "Rating Distribution",
      description: "Breakdown of ratings across scale points",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Rolling Average",
      description: "Smoothed average over recent sessions",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Rating Trend",
      description: "Change in ratings over sessions",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Trials to Criterion",
      description: "Trials recorded so far in Baseline/Intervention",
    },
  ],
  "magnitude|net": [
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Daily Performance Rating",
      description: "Average performance rating per session",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "rating-distribution",
      title: "Rating Distribution",
      description: "Breakdown of ratings across scale points",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Rolling Average",
      description: "Smoothed average over recent sessions",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Performance Trend",
      description: "Change in performance quality over time",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "trials-to-criterion",
      title: "Opportunities to Criterion",
      description: "Opportunities recorded so far in Baseline/Intervention",
    },
  ],
  "magnitude|free-operant": [
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Mean Naturalistic Rating",
      description: "Average rating of naturally occurring behavior",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      id: "rating-distribution",
      title: "Rating Distribution",
      description: "Breakdown of ratings across scale points",
    },
    {
      icon: ANALYTICS_ICONS.chart,
      title: "Rolling Average",
      description: "Smoothed average over recent sessions",
    },
    {
      icon: ANALYTICS_ICONS.trend,
      id: "trend",
      title: "Rating Trend",
      description: "Change in ratings over time",
    },
  ],
};
